export default class Facets {
  constructor(elementId) {
    this.facets = {};
    this.wrapper = document.getElementById(elementId);

    if (this.wrapper) {
      this.spinner = this.wrapper.querySelector("#fs-spinner");
      this.form = this.wrapper.querySelector("#fs-form");
      this.resetFacets = this.wrapper.querySelector("#fs-reset");
      this.facetsTerms = this.wrapper.querySelectorAll(".fs-option");
      this.searchInput = this.wrapper.querySelector("#fs-search");
      this.facetOrder = this.wrapper.querySelector("#fs-order");
      this.facetDefaultSortOrder = this.facetOrder.value;
      this.facetResults = this.wrapper.querySelector("#fs-results");
      this.facetOrderValue = this.wrapper.querySelector("#fs-order-value");
      this.currentPage = parseInt(new URLSearchParams(window.location.search).get("_page"), 10) || 1;
      this.facetPagination = this.wrapper.querySelector("#fs-pagination");
    } else {
      console.error(`Wrapper with id ${elementId} not found.`);
    }
  }

  init() {
    this.setupSearchInput();
    this.setupFacetListeners();
    this.setupFacetOrder();
    this.clearFacets();
    this.syncFacetsFromUrl();
    this.scrollToFacets();
    this.fetchSearchResults();
  }

  scrollToFacets() {
    if (this.facetResults) {
      document.addEventListener("DOMContentLoaded", () => {
        if (this.currentPage > 1) {
          document.getElementById("filterEntities").scrollIntoView({ behavior: "smooth" });
        }
      });
    }
  }

  resetPagination() {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete("_page");
    window.history.replaceState({}, "", currentUrl);

    this.currentPage = 1;
  }

  updateURLParams(key, value) {
    const url = new URL(window.location.href);

    if (key === "page") {
      this.currentPage = parseInt(value, 10);
      if (this.currentPage > 1) {
        url.searchParams.set("_page", this.currentPage);
      } else {
        url.searchParams.delete("_page");
      }
    } else {
      const urlKey = `_${key}`;
      if (value && value.length > 0) {
        url.searchParams.set(urlKey, encodeURIComponent(value));
      } else {
        url.searchParams.delete(urlKey);
      }
    }

    window.history.pushState({}, "", url);
  }

  setupSearchInput() {
    if (!this.searchInput) return;

    const debounce = (fn, delay) => {
      let timeoutId;
      return (...args) => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
      };
    };

    this.searchInput.addEventListener("input", debounce(() => {
      const searchValue = this.searchInput.value.trim();

      if (searchValue.length >= 3) {
        this.updateURLParams("search", searchValue);
        this.fetchSearchResults();
      } else if (searchValue.length === 0) {
        this.updateURLParams("search", null);
        this.fetchSearchResults();
      }
    }, 200));
  }

  setupFacetOrder() {
    if (!this.facetOrder) return;

    this.facetOrder.addEventListener("change", () => {
      const order = this.facetOrder.value;
      this.facetOrderValue.value = order;
      this.updateURLParams("order", order);
      this.fetchSearchResults();
    });
  }

  setupFacetListeners() {
    if (!this.facetsTerms) return;

    this.facetsTerms.forEach((term) => {
      term.addEventListener("change", () => {
        const facetName = term.name;
        const facetValue = term.value;

        if (term.checked) {
          if(this.currentPage > 1) {
            this.resetPagination();
          }

          if (!this.facets[facetName]) {
            this.facets[facetName] = [];
          }
          if (!this.facets[facetName].includes(facetValue)) {
            this.facets[facetName].push(facetValue);
          }
        } else {
          this.facets[facetName] = this.facets[facetName].filter(
            (value) => value !== facetValue
          );
          if (this.facets[facetName].length === 0) {
            delete this.facets[facetName];
          }
        }

        this.fetchSearchResults();
        this.updateURLParams(facetName, this.facets[facetName]);
      });
    });
  }

  syncFacetsFromUrl() {
    const url = new URL(window.location.href);
    const urlParams = url.searchParams;

    if (![...urlParams.keys()].length) return;

    if (urlParams.has("_search")) {
      this.searchInput.value = urlParams.get("_search");
    }

    if (urlParams.has("_order")) {
      this.facetOrderValue.value = urlParams.get("_order");
      this.facetOrder.value = urlParams.get("_order");
    }

    urlParams.forEach((value, key) => {
      if (!key.startsWith("_")) return;

      const facetName = key.slice(1).replace(/\[\]$/, "");
      const facetValues = value.split("%2C").map(decodeURIComponent);

      this.facets[facetName] = facetValues;

      facetValues.forEach((facetValue) => {
        const checkbox = this.wrapper.querySelector(
          `input[name="${facetName}[]"][value="${facetValue}"]`
        );
        if (!checkbox) return;

        checkbox.checked = true;
      });
    });
  }

  clearFacets() {
    if (!this.resetFacets) return;

    this.resetFacets.addEventListener("click", () => {
      this.facets = {};

      const url = new URL(window.location.href);
      const keysToDelete = [];

      url.searchParams.forEach((_, key) => {
        if (key.startsWith("_")) {
          keysToDelete.push(key);
        }
      });

      keysToDelete.forEach((key) => {
        url.searchParams.delete(key);
      });

      url.searchParams.delete("_search");
      window.history.pushState({}, "", url);

      this.facetsTerms.forEach((term) => {
        term.checked = false;
      });

      if (this.currentPage > 1) {
        this.resetPagination();
      }

      if (this.searchInput) {
        this.searchInput.value = "";
      }

      if (this.facetOrder) {
        this.facetOrder.value = this.facetDefaultSortOrder;
      }

      this.fetchSearchResults();
    });
  }

  fetchSearchResults(page = null) {
    this.spinner.classList.remove("hidden");
    this.facetResults.classList.add("blur-md", "overflow-hidden");

    const formData = new FormData(this.form);
    formData.append("action", "ajaxFilter");
    formData.append("paged", page || this.currentPage);
    formData.append("current_url", window.location.href);

    const request = new XMLHttpRequest();
    request.open("POST", ajax.ajaxUrl, true);
    request.setRequestHeader(
      "Content-Type",
      "application/x-www-form-urlencoded; charset=UTF-8"
    );

    request.onload = () => {
      if (request.status >= 200 && request.status < 400) {
        const data = JSON.parse(request.responseText);
        this.facetResults.classList.remove("blur-md", "overflow-hidden");
        this.spinner.classList.add("hidden");
        if (data.data.message) {
          this.facetResults.innerHTML = data.data.message;
          this.facetPagination.innerHTML = "";
          return;
        }
        this.facetResults.innerHTML = data.data.posts;
        this.buildPagination(data);
      }
    };

    request.send(new URLSearchParams(formData));
  }

  buildPagination(data) {
    const totalPages = parseInt(data.data.total);
    const currentPage = parseInt(data.data.current);

    if (totalPages <= 1) {
      this.facetPagination.innerHTML = "";
      return;
    }

    let paginationHTML = "<ul class='pagination'>";
    const pageRange = 5;

    const startPage = Math.max(1, currentPage - Math.floor(pageRange / 2));
    const endPage = Math.min(totalPages, startPage + pageRange - 1);

    const adjustedStartPage = Math.max(1, endPage - pageRange + 1);

    if (adjustedStartPage > 1) {
      paginationHTML += this.generatePageLink(1, currentPage);
      if (adjustedStartPage > 2) {
        paginationHTML += `<li><span>...</span></li>`;
      }
    }

    for (let i = adjustedStartPage; i <= endPage; i++) {
      paginationHTML += this.generatePageLink(i, currentPage);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        paginationHTML += `<li><span>...</span></li>`;
      }

      paginationHTML += this.generatePageLink(totalPages, currentPage);
    }

    paginationHTML += "</ul>";
    this.facetPagination.innerHTML = paginationHTML;

    this.setupPaginationLinks();
  }

  generatePageLink(page, currentPage) {
    const isActive = page === currentPage ? "current" : "";

    return `
      <li>
        <a href="#" class="page-numbers ${isActive}" data-page="${page}">
          ${page}
        </a>
      </li>
    `;
  }

  setupPaginationLinks() {
    document.querySelectorAll(".page-numbers").forEach((link) => {
      link.addEventListener("click", (event) => {
        event.preventDefault();
        const page = parseInt(event.target.dataset.page, 10);

        document.getElementById("filterEntities").scrollIntoView({ behavior: "smooth" });


        this.updateURLParams("page", page);

        document.querySelectorAll(".page-numbers").forEach((link) => {
          link.classList.remove("current");
        });
        event.target.classList.add("current");

        this.fetchSearchResults(page);
      });
    });
  }
}


if(document.getElementById('fs-entities')) {
  const facetsInstance = new Facets('fs-entities')
  facetsInstance.init()
}
